/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ContactTermsReceivables,
  CostCenterFinderListItemDto,
  ExcludedCcRangeDto,
  PrinterDto,
  SystemVarsFranchiseeDto,
  SystemVarsManagerDto,
  SystemVarsManagerStatisticDto,
  SystemVarsReceivablesBaseDto,
  SystemVarsReceivablesDto,
} from '@gh/builder-shared-data';
import {
  bindUniqueId,
  composeSetters,
  createArrayHelper,
  EntityConfig,
  entityStateMerger,
  entityValidationSetter,
  entityValidatorByEntityConfig,
  errorsEntityState,
  fieldStateByEntityConfig,
  fieldValidatorByEntityConfig,
  getDefaultEmail,
  getEmailLocalName,
  isEntityInvalid,
  setEntityField,
  setEntityFields,
} from '@gh/common-shared';
import {
  AutocompleteItem,
  ID,
  withDisplayAsNameAndDescription,
  WithEntityState,
  withoutDisplay,
  WithUniqueId,
} from '@gh/common-shared-data';
import { OptimisticLockingSupport, PrivilegedTokenSupport } from '@gh/core-data';
import { cast, isNotNil, valueComparator } from '@gh/core-util';

import { includes, map, omit, some } from 'lodash';

export class SystemVarsFranchisee implements OptimisticLockingSupport, PrivilegedTokenSupport {
  constructor(public id: Maybe<ID>,
              public email: string,
              public formedDate: Date,
              public companyName: string,
              public displayName: string,
              public usersFromOtherOfficesAccess: boolean,
              public isUsersFromOtherOfficesAccessActive: boolean,
              public usersEmailAccessFromOtherOffices: boolean,
              public isUsersEmailAccessFromOtherOfficesActive: boolean,
              public publishToWeb: boolean,
              public isPublishToWebAvailable: boolean,
              public typeOfBusiness?: string,
              public systemName?: string,
              public taxId?: ID,
              public tradingAs?: string,
              public abn?: string,
              public gst?: string,
              public acn?: string,
              public street1?: string,
              public city?: string,
              public state?: string,
              public postCode?: string,
              public street2?: string,
              public appliesBuilderLicenceNo?: boolean,
              public licenseNo?: string,
              public phoneOffice?: string,
              public fax?: string,
              public etag?: string,
              public phoneMobile?: string,
              public privilegedTokens?: Hash<string>,
              public fee?: number,
              public hasFeeUpdatePermission?: boolean,
  ) {
  }
}

export function systemVarsFranchiseeFromDto(dto: SystemVarsFranchiseeDto): SystemVarsFranchisee {
  return {
    ...dto,
    appliesBuilderLicenceNo: !!dto.licenseNo,
    email: dto.email && getEmailLocalName(dto.email),
    fee: dto.fee,
    hasFeeUpdatePermission: dto.hasFeeUpdatePermission,
    usersEmailAccessFromOtherOffices: Boolean(dto.usersEmailAccessFromOtherOffices),
    isUsersEmailAccessFromOtherOfficesActive: dto.isUsersEmailAccessFromOtherOfficesActive !== false,
  };
}

export function systemVarsFranchiseeToDto(model: SystemVarsFranchisee): SystemVarsFranchiseeDto {
  return cast<SystemVarsFranchiseeDto>(omit(
    {
      ...model,
      email: model.email && getDefaultEmail(model.email),
      licenseNo: model.appliesBuilderLicenceNo ? model.licenseNo : void 0,
      fee: model.fee
    },
    'appliesBuilderLicenceNo'));
}

export type Printer = PrinterDto;
export type Printers = {
  isCloud: boolean;
  cloudPrinters: PrinterDto[];
};

export type ExcludedCcRange = WithEntityState<WithUniqueId<ExcludedCcRangeDto>>;
export type SystemVarsReceivables = SystemVarsReceivablesBaseDto & {
  excludedCostCenters: ExcludedCcRange[];
};

export const systemVarsReceivablesFromDto = (dto: SystemVarsReceivablesDto): SystemVarsReceivables => ({
  ...dto,
  excludedCostCenters: map(dto.excludedCostCenters, (range) => bindUniqueId({
    ...range,
    from: withDisplayAsNameAndDescription(range.from),
    to: withDisplayAsNameAndDescription(range.to),
  })),
});

export const systemVarsReceivablesToDto = (model: SystemVarsReceivables): SystemVarsReceivablesDto => ({
  ...model,
  excludedCostCenters: map(model.excludedCostCenters, (range) => <ExcludedCcRangeDto>omit(
    { ...range, from: withoutDisplay(range.from), to: withoutDisplay(range.to) },
    ['uniqueId', 'entityState'])),
});

export const getExcludedCcRangeId = (range: ExcludedCcRange) => range.uniqueId;

const EXCLUDED_CC_RANGE_ENTITY_CONFIG: EntityConfig<ExcludedCcRange> = {
  from: {
    required: true,
  },
  to: {
    required: true,
    editable: (entity) => isNotNil(entity.from),
  },
};

export const excludedCcRangeFieldState = fieldStateByEntityConfig(EXCLUDED_CC_RANGE_ENTITY_CONFIG);
export const validateExcludedCcRangeField = fieldValidatorByEntityConfig(EXCLUDED_CC_RANGE_ENTITY_CONFIG);
export const validateExcludedCcRange = entityValidatorByEntityConfig(EXCLUDED_CC_RANGE_ENTITY_CONFIG);
export const complementExcludedCcRange = entityStateMerger(errorsEntityState(validateExcludedCcRange));
export const hasInvalidExcludedCcRange = (details: SystemVarsReceivables) =>
  some(details.excludedCostCenters, isEntityInvalid);
export const excludedCcRangeArr = createArrayHelper(getExcludedCcRangeId);

export const newExcludedCcRange = (): ExcludedCcRange => complementExcludedCcRange(<any>bindUniqueId({}));
const setRangedField = (entity: ExcludedCcRange, name: keyof ExcludedCcRange, value: any) =>
  name === 'from'
    ? setEntityFields(entity, {
      from: value,
      to: value,
    })
    : setEntityField(entity, name, value);

export const setExcludedCcRangeField = composeSetters(setRangedField, entityValidationSetter(validateExcludedCcRange));

export const excludedCcRangeCcComparator = valueComparator<AutocompleteItem>((cc) =>
  cc && cc.name ? Number(cc.name) : -1);

export type SystemVarsManager = SystemVarsManagerDto;
export type SystemVarsManagerStatistic = SystemVarsManagerStatisticDto;

export function systemVarsManagerToDto(model: SystemVarsManager): SystemVarsManagerDto {
  const { commissionCc, interestCc } = model;
  return {
    ...model,
    commissionCc: commissionCc && <CostCenterFinderListItemDto>omit(commissionCc, ['suppliers']),
    interestCc: interestCc && <CostCenterFinderListItemDto>omit(interestCc, ['suppliers']),
  };
}

export function systemVarsManagerFromDto(dto: SystemVarsManagerDto): SystemVarsManager {
  return dto;
}

export function isSystemVarsDayControlsByContactTermsEnabled(paymentDue: ContactTermsReceivables): boolean {
  const disablePaymentDueList = [
    ContactTermsReceivables.NoneAdvised,
    ContactTermsReceivables.COD,
    ContactTermsReceivables.Prepaid,
  ];
  return !includes(disablePaymentDueList, paymentDue);
}
